import { SectionHead, UsersTable } from '../../components';
import {
  GET_INVITATION_LINK,
  GET_PARTNER_CLIENT_ACCOUNT_EMPLOYEES_NEW,
} from '@/common/actions/get';
import { DELETE_PARTNER_CLIENT_ACCOUNT_EMPLOYEE } from '@/common/actions/delete';
import {
  activeTab,
  getVariables,
  initialDeletedMemberState,
  queryParamsInitial,
  usersInstructionTypeForFilter,
} from '../../store';
import { useTableQueryParams } from '../../store/useTableQueryParams';
import { ModalInviteUser } from '../../modals/ModalInviteUser';
import { ModalLinkUser } from '../../modals/ModalLinkUser';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Box, ConfirmModal } from '@servicepipe/ui-components';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

export const UsersView = ({ isShowTable }) => {
  const { id: partnerClientAccountId } = useParams();
  const {
    searchParams,
    setSearchParams,
    queryParams,
    onPageChange,
    onSortingChange,
    onFiltersChange,
  } = useTableQueryParams(queryParamsInitial);

  const [isShowModalInviteMember, setIsShowModalInviteMember] = useState(false);
  const [deletedMember, setDeletedMember] = useState(initialDeletedMemberState);
  const [link, setLink] = useState(null);
  const [currentVariables, setCurrentVariables] = useState();

  const [getMembers, { loading, refetch, data }] = useLazyQuery(
    GET_PARTNER_CLIENT_ACCOUNT_EMPLOYEES_NEW
  );
  const [getInvitationLink] = useLazyQuery(GET_INVITATION_LINK);
  const [deleteMember, { loading: loadingDeleteMember }] = useMutation(
    DELETE_PARTNER_CLIENT_ACCOUNT_EMPLOYEE,
    {
      onCompleted: () => {
        if (currentVariables) {
          refetch(currentVariables);
        }
        setDeletedMember(initialDeletedMemberState);
        toast.success('Success');
      },
      onError: (error) => toast.error(error.message),
    }
  );

  useEffect(() => {
    if (queryParams.tab !== activeTab.users || !partnerClientAccountId) {
      return;
    }

    const searchParamsDeletedAt = searchParams.get('deletedAt');
    const baseVariables = {
      ...getVariables(queryParams, usersInstructionTypeForFilter),
      partnerClientAccountId: [Number(partnerClientAccountId)],
    };
    const variables = searchParamsDeletedAt
      ? baseVariables
      : { ...baseVariables, deletedAt: queryParams.deletedAt ? undefined : null };

    setCurrentVariables(variables);
    getMembers({ variables });
  }, [queryParams, partnerClientAccountId]);

  const handleToggleDeleted = (event) => {
    const isChecked = event.target.checked;
    searchParams.set('page', String(queryParamsInitial.page));
    searchParams.set('deletedAt', String(isChecked));
    setSearchParams(searchParams, { replace: true });
  };

  const handleGetLinkByMember = (id) => {
    getInvitationLink({
      variables: { partnerClientAccountEmployeeId: id },
      onCompleted: (response) => setLink(response.getInvitationLink.invitationLink),
      onError: (error) => toast.error(error.message),
    });
  };

  if (!isShowTable) {
    return null;
  }

  return (
    <Box padding="0" gap="40px" direction="column">
      <SectionHead
        title="Users"
        checkboxLabel="Show deleted users"
        checkboxChecked={queryParams.deletedAt === true || queryParams.deletedAt === 'true'}
        onCheckboxChange={handleToggleDeleted}
        buttonLabel="Invite user"
        onButtonClick={() => setIsShowModalInviteMember(true)}
      />

      <UsersTable
        data={data?.partnerClientAccountEmployees?.items || []}
        onPageChange={onPageChange}
        onSortingChange={onSortingChange}
        onFiltersChange={onFiltersChange}
        queryParams={queryParams}
        totalPages={data?.partnerClientAccountEmployees?.info?.totalPages || 1}
        totalCount={data?.partnerClientAccountEmployees?.info?.totalCount || 0}
        loading={loading}
        setDeletedMember={setDeletedMember}
        handleGetLinkByMember={handleGetLinkByMember}
      />

      <ModalInviteUser
        isShow={isShowModalInviteMember}
        onToggle={() => setIsShowModalInviteMember((prev) => !prev)}
        onRefetch={() => {
          if (currentVariables) {
            getMembers({ variables: currentVariables, fetchPolicy: 'network-only' });
            return;
          }

          refetch();
        }}
      />

      <ModalLinkUser isShow={Boolean(link)} link={link} onClose={() => setLink(null)} />

      {deletedMember.deleted && (
        <ConfirmModal
          title="Delete user"
          size="s"
          toggle={() => setDeletedMember(initialDeletedMemberState)}
          onConfirm={() => deleteMember({ variables: { id: deletedMember.id } })}
          isLoading={loadingDeleteMember}
          confirmButtonText="Delete"
        >
          {`Are you sure that you want to delete ${deletedMember.email} from Account ID ${partnerClientAccountId}?`}
        </ConfirmModal>
      )}
    </Box>
  );
};

export default UsersView;
